import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';

import Header from 'components/Header/Header';
import SideBar from 'components/SideBar/SideBar';
import { useRfp, useRfpDataRoom } from 'api';
import { ArrowLeftIcon } from 'components/SvgIcons';
import Markdown from 'components/Markdown/Markdown';
import ProjectInfoPanel from 'components/ProjectInfoPanel/ProjectInfoPanel';
import useFormattedDate, { FULL_MONTH_WITH_TIME_AND_TIMEZONE } from 'hooks/useFormattedDate';
import CollapsibleSection from 'components/CollapsibleSection/CollapsibleSection';
import Loader from 'components/Loader/Loader';
import LinkButton from 'components/LinkButton/LinkButton';

import './DataRoomPage.scss';

export interface DataRoomProps {}

const DataRoomPage = ({}: DataRoomProps) => {
  const { t } = useTranslation();
  const { rfpId } = useParams<{ rfpId: string }>();

  const { data: rfp, isLoading: isRfpLoading } = useRfp(Number(rfpId));
  const { data: categories, isLoading: areCategoriesLoading } = useRfpDataRoom(Number(rfpId));

  const openDate = useFormattedDate(rfp?.open_date ?? '', FULL_MONTH_WITH_TIME_AND_TIMEZONE);
  const closeDate = useFormattedDate(rfp?.response_close_date ?? '', FULL_MONTH_WITH_TIME_AND_TIMEZONE);

  if (isRfpLoading || areCategoriesLoading) {
    return (
      <div className="enkrateia-data-room-page loader-overlay">
        <Loader />
      </div>
    );
  }

  return (
    <div className="enkrateia-data-room-page">
      <Header />
      <div className="content">
        <SideBar variant="prosfora" collapsible>
          <ProjectInfoPanel variant="prosfora" label={t('pages.prosforaProject.openDate')} value={openDate} />
          <ProjectInfoPanel variant="prosfora" label={t('pages.prosforaProject.closeDate')} value={closeDate} />
        </SideBar>
        <main>
          <div className="head">
            <Link to={`/rfp/${rfpId}`}>
              <ArrowLeftIcon size={14} color="var(--primaryBlue100)" />
              {t('pages.dataRoom.goBackToOverview')}
            </Link>
            <div className="head-content">
              <div className="description">
                <h1>{t('pages.dataRoom.title')}</h1>
                <Markdown linkTarget="_blank" className="context-0">
                  {rfp?.rfp_description ?? ''}
                </Markdown>
              </div>
            </div>
          </div>
          <div className="body">
            <div className="title">
              <h2>{t('pages.dataRoom.categories')}</h2>
              <LinkButton variant="secondary" to={`/rfp/${rfpId}/data-room-export`} target="_blank">
                {t('pages.dataRoom.downloadAll')}
              </LinkButton>
            </div>
            {categories?.map((category) => (
              <CollapsibleSection title={`${category.category_name} (${category.data_room_category_files.length})`}>
                <Markdown>{category.category_text}</Markdown>
                <ul>
                  {category.data_room_category_files
                    .filter((file) => file.show_directly)
                    .map((file) => (
                      <li key={file.id}>
                        <a href={file.url} target="_blank" rel="noopener noreferrer">
                          {file.file_name}
                        </a>
                      </li>
                    ))}
                </ul>
              </CollapsibleSection>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DataRoomPage;
